import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardReferenceSchema,
} from './board';
import { cardFieldOverridePayloadSchema } from './card-field';
import { commentStreamCommentSchema } from './comment';
import { ApiError } from './errors';
import {
  type EVENT_TYPES,
  EVENT_TYPE_NAMES,
  type EventType,
  surfacesInFeed,
} from './events';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';

/**
 * Activity feeds — the contract layer for block **B28**.
 *
 * `BE-28.1` (`FR-COLLAB-017`, the card's one reverse-chronological stream),
 * `BE-28.2` (`FR-COLLAB-018`, the phrasing grammar — this file's own item) and
 * `BE-28.3` (`FR-COLLAB-020`, the board feed). `FR-COLLAB-019`'s *toggle*
 * belongs to `FE-28.1`; what B28 contributes to it is `?filter=`, declared
 * below, which is what makes the toggle expressible at all.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS: A VOCABULARY AND A WIRE SHAPE, NOT A RENDERER
 * ---------------------------------------------------------------------------
 * Three things live here and nowhere else:
 *
 *   1. **`FEED_EVENT_TYPES`** — which of the registry's event names a human
 *      ever sees. Derived from `./events`, never written out.
 *   2. **`ACTIVITY_TEMPLATES`** — one translatable sentence template per
 *      *template key*, with `{slot}` placeholders. The table is the grammar.
 *   3. **The wire shapes** an activity page is served in, plus this package's
 *      fourth keyset cursor codec.
 *
 * What does **not** live here: the mapping from a stored `activity_entries`
 * row to a `{ templateKey, tokens }` pair. That is `apps/server/src/activity/
 * phrase.ts`, deliberately pure, so all 64 types are assertable with no
 * container running. This file declares the *contract* that module must
 * satisfy, and `activity.test.ts` pins it as an explicit slot-per-key map so a
 * template gaining a slot fails a test rather than rendering `undefined` into
 * somebody's history.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ (a) WHY `templateKey` IS ON THE WIRE *BESIDE* `type`, WHICH LOOKS LIKE
 * THE SECOND ENCODING `./cross-board-move` REFUSED
 * ---------------------------------------------------------------------------
 * B21 declined to put a `direction` field on its move report on the grounds
 * that it would be "a second encoding of a fact the row already carries", and
 * a reader who has absorbed that rule will read `templateKey` beside `type` as
 * the same mistake. **It is not, and the difference is mechanical: the mapping
 * is not one-to-one.** Four event types render **two different sentences**
 * each, chosen by inspecting the payload:
 *
 *   - `card.moved_between_boards` and `list.moved_between_boards` — B21 writes
 *     **two rows per gesture, one per board, with disjoint payloads**; the
 *     departure row carries `previousPosition` and the arrival row `position`.
 *   - `due.date_changed` — two routes emit this one name with **identical key
 *     sets**, and "changed the due date" is false when only the reminder moved.
 *   - `card.description_edited` — "edited the description" is false when it was
 *     removed.
 *
 * So `type` records *what was written* and `templateKey` records *what it
 * means*. A client keying phrasing off `type` alone renders the wrong sentence
 * for a cross-board move in exactly one direction — which is the failure B21's
 * two-row payload split exists to prevent, arriving one layer up. The four
 * splits are enumerated in `ACTIVITY_SPLIT_EVENT_TYPES`; `ActivityTemplateKey`
 * `Exclude`s their bare names, so a split type has **two** keys and no third,
 * and `templateKey: 'due.date_changed'` does not typecheck anywhere.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ (b) WHY THERE IS NO RENDERED `sentence` STRING ON THE WIRE
 * ---------------------------------------------------------------------------
 * `FR-COLLAB-018`'s edge case is verbatim: *"Phrasing must be authored as
 * translatable sentence templates rather than string concatenation."* A
 * server-rendered English string **is** that concatenation, shipped one layer
 * earlier and one layer harder to translate — the client would have no way back
 * to the parts. So the entry carries `templateKey` + `tokens` and the client
 * assembles.
 *
 * `renderActivitySentence()` below exists all the same, and its two callers are
 * the reason it is exported rather than kept in a test file: this package's own
 * tests, and `apps/web/e2e/FR-COLLAB-018.spec.ts`, which asserts prose over HTTP
 * with no UI built. ⚠ **It is not for the server to call on a response path.**
 * Adding `sentence: renderActivitySentence(entry)` to `activityEntrySchema`
 * would satisfy every test in this file and quietly reintroduce exactly what the
 * edge case forbids.
 *
 * ---------------------------------------------------------------------------
 * ⚠ (c) WHY THE CURSOR CODEC IS A **FOURTH** COPY AND IMPORTS NOTHING
 * ---------------------------------------------------------------------------
 * `./comment` (B16), `./notification` (B17) and `./board-archive` (B22) each
 * declare the same twenty lines, and each records the same reason: **the shape
 * is the reusable thing, not the function.** A shared codec couples two tables'
 * sort keys, and the day one changes the other changes silently — a paging
 * stream that skips or repeats rows with a 200 and a well-formed body.
 * `./board-archive`'s header calls its own copy "the design, not an oversight",
 * and `./notification`'s predicted that a third caller would finally extract it;
 * a fourth has now not. Read those two headers before extracting this one.
 *
 * ⚠ **This copy carries `./notification`'s `POSTGRES_TIMESTAMPTZ` guard, which
 * `./comment`'s original four refusals do not have**, and the hole it closes is
 * measured rather than hypothetical: a cursor carrying `'notatime'` decodes
 * cleanly through base64url, passes a bare `z.string()` payload check, reaches
 * `'notatime'::timestamptz` in the `WHERE`, and turns the one function whose
 * whole job is to produce a **422** into a **500**. See `decodeActivityCursor`.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ COMPILE-TIME TOTALITY IS THE POINT OF THE TYPE-LEVEL DERIVATION, AND THE
 * OBVIOUS SPELLING SILENTLY GIVES NONE OF IT
 * ---------------------------------------------------------------------------
 * The design note writes `type FeedEventType = (typeof FEED_EVENT_TYPES)[number]`
 * over `EVENT_TYPE_NAMES.filter(surfacesInFeed)`. Written literally, `.filter()`
 * returns `EventType[]`, so that union is **the whole registry** — every name,
 * feed or not — and `Record<ActivityTemplateKey, string>` then demands templates
 * for `skeleton.noop` and `board.member_added` while demanding nothing at all
 * for a name whose flag flips later. The one property this table exists to have
 * would be absent, and nothing would be red.
 *
 * So the union is derived at the **type** level from `EVENT_TYPES` itself
 * (`FeedEventName`, below), which is `as const`, so each `surfacesInFeed` is the
 * literal `true` or `false` rather than `boolean`. Two consequences worth
 * knowing before touching either file:
 *
 *   - **Flipping a `surfacesInFeed` in `./events` breaks this file at compile
 *     time** — a name turned on has no template, a name turned off has a
 *     surplus one. That is the whole design; the alternative is an event that
 *     silently never appears in anybody's feed.
 *   - **Dropping `as const` from `EVENT_TYPES` collapses `FeedEventName` to
 *     `never`** (because `boolean extends true` is false), and the 72 entries
 *     below all become surplus keys. Loud, and worth recognising for what it is.
 *
 * The runtime array is filtered through a type guard rather than cast, so the
 * value and the type are derived from the same predicate.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE ONE PLACE THE DESIGN NOTE CONTRADICTS ITSELF, RECORDED RATHER THAN
 * SILENTLY RESOLVED: `card.renamed` AND THE `previousTitle` THAT MAY NOT EXIST
 * ---------------------------------------------------------------------------
 * `FR-COLLAB-018` names *"renamed this card (from &lt;old name&gt;)"* as
 * canonical, and B28 adds `previousTitle` to `card.renamed`, `list.renamed` and
 * `board.renamed`'s payloads (additive JSONB, no migration) so it can be
 * rendered. **Entries written before that change do not carry it**, and they are
 * the whole historical corpus.
 *
 * The design note's §7 proposes that the template *branch* — `renamed this card
 * to {title}` when `previousTitle` is absent — while its §5b fixes the split
 * list at **four** and a `Record<ActivityTemplateKey, string>` holds exactly one
 * string per key. Both cannot hold. This file takes §5b: there are four splits,
 * `card.renamed` has one template, and the legacy case is handled where §5g
 * already says it is handled — **rule 3, a neutral placeholder with
 * `navigable: false`**, produced by `phrase.ts` for any object whose name can be
 * recovered neither from a live row nor from a payload snapshot. The sentence
 * degrades to "renamed this card (from an earlier name)" rather than to a
 * different sentence, and no key is invented that the grammar's own
 * reachability test would not know about.
 *
 * ⚠ **Do not "fix" this by adding `card.renamed.from` / `card.renamed.to`.** A
 * fifth split would be a template key `apps/server/src/activity/phrase.ts` never
 * emits, which its `FR-COLLAB-018.spec.ts` asserts against.
 *
 * ---------------------------------------------------------------------------
 * IT DECLARES NO `BE-02.1` NOUN AND NO COLUMN CONSTANT
 * ---------------------------------------------------------------------------
 * "Activity" is not one of `BE-02.1`'s eight nouns, and the count this package's
 * `./index` header keeps has gone wrong repeatedly by treating "but it is a real
 * table with real columns" as an argument — `activity_entries` is as real as
 * `checklists` was at B26 and `attachments` at B27, and neither moved it. Seven
 * of the eight have a file; **membership** is still the one that does not, and
 * `ls packages/shared/src` is still the instruction.
 *
 * No column constant either, on `./cross-board-move`'s and `./board-archive`'s
 * precedent: B28 introduces no column and writes none. `ACTIVITY_PAGE_SIZE_*`
 * bound a **request**, `./comment`'s `COMMENT_PAGE_SIZE_*` precedent exactly,
 * and `ACTIVITY_COLLAPSE_WINDOW_MS` is a product decision about rendering, not
 * a bound over anyone's data.
 */

// ---------------------------------------------------------------------------
// Which events a human ever sees — BE-03.4's discriminator, read in code.
// ---------------------------------------------------------------------------

/**
 * The event names that surface in the user-visible feed, as a **type**.
 *
 * ⚠ **This is the narrowing `EVENT_TYPE_NAMES.filter(surfacesInFeed)` cannot
 * give you** — see the header. `EVENT_TYPES` is `as const`, so each declaration's
 * `surfacesInFeed` is the literal `true` or `false`, and this mapped type keeps
 * exactly the keys whose literal is `true`. 68 names today — 56 through B30,
 * plus B31's `card.field_overridden` (`FR-EXP-031` AC4), B35's
 * `card.copied_from_card` (`FR-TMPL-013`), B36's three board-template events
 * (`board.template_marked`, `board.template_unmarked`,
 * `board.template_description_saved` — `FR-TMPL-001`/`004`/`007`), B37's
 * three card-template events (`card.template_marked`,
 * `card.template_unmarked`, `card.created_from_template` —
 * `FR-TMPL-008`/`009`/`012`) and B38's two invite-link events
 * (`board.invite_link_created`, `board.invite_link_revoked` —
 * `FR-PERM-019`/`020`), B42's `board.background_changed`
 * (`FR-EXP-025` / `FR-BOARD-002`), and B43's `card.added_to_list`
 * (`FR-NOTIF-018`). ⚠ **A number in this prose is a snapshot
 * and the mapped type is the authority**; recount against
 * `FEED_EVENT_TYPES.length` rather than trusting it.
 */
type FeedEventName = {
  [K in EventType]: (typeof EVENT_TYPES)[K]['surfacesInFeed'] extends true ? K : never;
}[EventType];

/**
 * Narrow an `EventType` to a `FeedEventName`, at runtime, using the registry's
 * own accessor.
 *
 * A type guard rather than a cast on the filtered array, so the value and the
 * type come from the same predicate: `surfacesInFeed()` reads `EVENT_TYPES`, and
 * `FeedEventName` reads `EVENT_TYPES`. A cast would let the two disagree.
 */
const isFeedEventName = (type: EventType): type is FeedEventName => surfacesInFeed(type);

/**
 * **The list B28's two queries put into `inArray(activityEntries.type, …)`, and
 * the reason `activity_entries` needs no discriminator column.**
 *
 * `packages/shared/src/events.ts`' header calls that column an `OUTSTANDING
 * MIGRATION`. B28 is the flag's first and only reader and **declines to land
 * it**: the registry is already the sole writer of `activity_entries.type`
 * (`insertEvents` parses through `eventInputSchema`, whose `z.enum` is built
 * from `EVENT_TYPE_NAMES`), so a column would be a second, backfillable encoding
 * of a fact the registry already holds — free to disagree with it the moment
 * either changed, and the disagreement invisible. The flag is authoritative in
 * code; the *result* is pushed into SQL.
 *
 * ⚠ **Derived, never hand-written.** A hand-copied list is the one way this can
 * drift from the registry, and the drift is silent in the worst direction: an
 * event simply never appears in anyone's feed. No status code, no missing field,
 * nothing red — just a gesture that leaves no trace a member can see.
 *
 * `readonly`, which `inArray`'s `ReadonlyArray` overload accepts as it stands.
 */
export const FEED_EVENT_TYPES: readonly FeedEventName[] =
  EVENT_TYPE_NAMES.filter(isFeedEventName);

/**
 * The type of a member of `FEED_EVENT_TYPES` — the design note's spelling,
 * which resolves to the narrow union above because the array is annotated.
 */
export type FeedEventType = (typeof FEED_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Template keys — the four types that are two sentences.
// ---------------------------------------------------------------------------

/**
 * The event names whose single `type` renders **two different sentences**,
 * chosen by inspecting the payload. See the header for why `templateKey` exists
 * at all; these four are the whole of the reason.
 *
 * | type | keys | discriminator |
 * |---|---|---|
 * | `card.moved_between_boards` | `.departed` / `.arrived` | `'position' in payload` |
 * | `list.moved_between_boards` | `.departed` / `.arrived` | `'position' in payload` |
 * | `due.date_changed` | `.date` / `.reminder` | `previousDueAt === dueAt` → `.reminder` |
 * | `card.description_edited` | `.edited` / `.cleared` | `payload.cleared === true` |
 *
 * ⚠ `satisfies readonly FeedEventName[]` is load-bearing: a typo, or a name
 * whose `surfacesInFeed` is `false`, is a compile error here rather than a bare
 * name that quietly stays a template key while its two split keys are added
 * beside it — which would leave the union with **three** keys for one type and
 * `phrase.ts` free to emit the one nothing renders.
 */
export const ACTIVITY_SPLIT_EVENT_TYPES = [
  'card.moved_between_boards',
  'list.moved_between_boards',
  'due.date_changed',
  'card.description_edited',
] as const satisfies readonly FeedEventName[];

type SplitEventName = (typeof ACTIVITY_SPLIT_EVENT_TYPES)[number];

/**
 * Every key `ACTIVITY_TEMPLATES` has a sentence for: the 68 feed event names,
 * **less** the four that split, **plus** their eight split keys. 72 today.
 *
 * ⚠ **"56 … 60" stood here through B30 and is corrected in place at B31**,
 * which registered `card.field_overridden` (`FR-EXP-031` AC4). Both numbers
 * moved by exactly one and the SPLIT set did not move at all: the new name is
 * one type with one sentence, and `phrase.ts` records why a
 * `.title` / `.description` split — the shape that would have moved the second
 * number by two and the first by one — is the wrong answer for it.
 *
 * ⚠ **"57 … 61" then stood through B34 and is corrected in place at B35**,
 * which registered `card.copied_from_card` (`FR-TMPL-013`, `BE-35.1`). Both
 * numbers moved by exactly one again and the SPLIT set again did not move: a
 * copy is one gesture with one sentence, and a copy that landed on a *different*
 * board is still the same sentence — B21's `.departed`/`.arrived` pair exists
 * because a move writes **two rows on two boards**, and a copy writes one row
 * on the destination alone, leaving the source untouched with nothing to
 * explain to its own feed.
 *
 * ⚠ **"58 … 62" then stood through B35 and is corrected in place at B36**,
 * which registered THREE at once — `board.template_marked`,
 * `board.template_unmarked` and `board.template_description_saved`
 * (`FR-TMPL-001`/`004`/`007`). Both numbers moved by exactly THREE and the
 * SPLIT set again did not move: none of the three renders two sentences —
 * the mark/unmark pair is one gesture each, in one direction each, and the
 * description event covers writing a value and clearing one to `null` with a
 * single sentence rather than a `.saved`/`.cleared` pair, on the judgement
 * that a template's description is a materially quieter facet than a card's
 * own body (`card.description_edited`'s own split). This is the first
 * correction in this table's history to move either number by more than one,
 * because it is the first time since B10 that every name a block registered
 * was new in every spelling at once, with nothing already admitted for it to
 * reuse.
 *
 * ⚠ **"61 … 65" then stood through B36 and is corrected in place at B37**,
 * which registered THREE at once again — `card.template_marked`,
 * `card.template_unmarked` and `card.created_from_template`
 * (`FR-TMPL-008`/`009`/`012`). Both numbers moved by THREE and the SPLIT set
 * again did not move, which is the interesting half rather than the
 * arithmetic: the third name is the one a reader would expect to split,
 * because `card.copied_from_card` renders the visually similar sentence and
 * B21's `.departed`/`.arrived` pair exists for a gesture that writes two
 * rows. It writes ONE row, on the created card alone — a template is left
 * untouched by being instantiated, so there is no second audience and no
 * second sentence — and it is a separate NAME rather than a second BRANCH of
 * `card.copied_from_card` for the two reasons `./events` argues at that
 * name's neighbour: the provenance key would never vary from `sourceCardId`,
 * and "copied this card from {card}" is false of an instantiation.
 *
 * ⚠ **"64 … 68" then stood through B37 and is corrected in place at B38**,
 * which registered TWO — `board.invite_link_created` and
 * `board.invite_link_revoked` (`FR-PERM-019`, `FR-PERM-020`, `BE-38.3`). Both
 * numbers moved by exactly TWO and the SPLIT set again did not move: each name
 * is one gesture in one direction, and the pair is `board.template_marked` /
 * `board.template_unmarked`'s shape one facet over rather than a lifecycle
 * needing a `.created`/`.replaced` split — regenerating a link emits the
 * *revoked* row and then the *created* row, two rows for two facts, which is
 * precisely why neither name needs two sentences.
 *
 * ⚠ **The interesting half of this correction is what B38 did NOT register.**
 * Three workspace-scoped join-request names (`FR-PERM-022`, `BE-38.4`) were
 * checked one at a time against the live constraint and **declined** —
 * `./events` carries the four-part argument, of which the two that decide it
 * are that `ck_activity_entries_scope` would need widening as well as
 * `ck_activity_entries_type` (a workspace entry has no board and no card), and
 * that `publishEvents` fans out on `board_id` alone so the rows could never
 * reach a socket. So a block whose whole subject is invitations and joining
 * added **two** feed sentences rather than five, and **nothing about a
 * workspace join request renders in any feed anywhere.** That is a decision
 * with a stated argument, not a template somebody forgot; a later block
 * reopening it owes both constraints, not one.
 *
 * ⚠ **`Exclude<FeedEventName, SplitEventName>` is what keeps a split type's bare
 * name off the union.** Written as a plain union with the eight added, all four
 * bare names would remain keys, the `Record` would demand four more templates,
 * and `phrase.ts` could emit `templateKey: 'due.date_changed'` — the exact
 * un-discriminated sentence the split exists to make unsayable. A split type has
 * two keys and no third.
 */
export type ActivityTemplateKey =
  | Exclude<FeedEventName, SplitEventName>
  | 'card.moved_between_boards.departed'
  | 'card.moved_between_boards.arrived'
  | 'list.moved_between_boards.departed'
  | 'list.moved_between_boards.arrived'
  | 'due.date_changed.date'
  | 'due.date_changed.reminder'
  | 'card.description_edited.edited'
  | 'card.description_edited.cleared';

// ---------------------------------------------------------------------------
// The grammar — FR-COLLAB-018, BE-28.2.
// ---------------------------------------------------------------------------

/**
 * **The translatable sentence templates.** One per `ActivityTemplateKey`, typed
 * as a **total** `Record` so a missing template is a compile error and a surplus
 * one is too.
 *
 * ---------------------------------------------------------------------------
 * THE THREE RULES EVERY TEMPLATE OBEYS
 * ---------------------------------------------------------------------------
 *   1. **A verb phrase, past tense, with NO actor slot.** `FR-COLLAB-018`:
 *      "Every entry renders as **actor plus** a past-tense verb phrase with a
 *      relative timestamp." The actor and the timestamp are rendered by the
 *      client from `actor` / `actorAutomationName` / `createdAt`, which is why
 *      `{actor}` appears in no template and must not be added — an actor
 *      interpolated into the middle of a sentence is untranslatable into any
 *      language whose word order differs from English, and the requirement's
 *      own phrasings all begin with the verb.
 *   2. **`this card` / `this board`, never the card's own name.** A card feed is
 *      already scoped to one card, and a board feed names the card **beside**
 *      the sentence (`activityEntrySchema.card`, `FR-COLLAB-020` AC1: "each
 *      entry naming the card it belongs to"). Putting the card's title in the
 *      sentence too would render it twice on the board feed.
 *   3. **Every `{slot}` must be produced by `phrase.ts` for that key, always.**
 *      There is no optional slot and no conditional syntax:
 *      `renderActivitySentence` throws on a slot with no token, deliberately, so
 *      that "this payload happened not to carry it" is caught in a test rather
 *      than shipped as an empty gap in a sentence. Where the underlying fact may
 *      be missing — a pre-B28 `previousTitle`, a hard-deleted checklist — §5g's
 *      rule 3 applies: `phrase.ts` supplies a neutral placeholder token with
 *      `navigable: false`. **The token is never omitted and the entry is never
 *      hidden** (`FR-COLLAB-018` AC3).
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `card.moved_between_boards.departed` NAMES NO DESTINATION, AND THAT IS A
 * PERMISSION DECISION RATHER THAN A GAP IN THE PHRASING
 * ---------------------------------------------------------------------------
 * `FR-COLLAB-018`'s canonical "moved this card to &lt;board&gt;" is the
 * **arrival** sentence, rendered from the arrival row's own `board_id` column.
 * The **departure** row deliberately carries no destination id: B21's payload
 * rule is *no payload key names a row that the audience of that row's own
 * `board_id` could not already see*, and the departure row's audience is the
 * source board. So the departure sentence is "moved this card to another board",
 * unnamed, and the two `.departed` templates below carry **no slot at all**.
 *
 * ⚠ **Do not close the gap by adding a payload key.** Naming the destination
 * would tell every member of the source board that a board exists which some of
 * them may not be able to see. The same holds for the list pair.
 *
 * ---------------------------------------------------------------------------
 * WHERE A TEMPLATE IS QUIETER THAN THE ROW IT RENDERS, AND WHY
 * ---------------------------------------------------------------------------
 *   - **`card.position_changed` and `list.position_changed` name no position.**
 *     `./events`' own note on the former records that B28 cannot render "moved
 *     left": a lexorank is not an index, and turning one into "third" needs the
 *     board's whole ordering *at the time*, which no row holds. "moved this
 *     card" is what is true. It is also what makes `FR-COLLAB-017`'s collapse
 *     edge case work at all — three quick drags render three byte-identical
 *     sentences and collapse into one.
 *   - **`checklist.item_edited`, `item_position_changed` and `item_removed` name
 *     no checklist**, while `item_added`, `item_completed`, `item_uncompleted`
 *     and `item_moved` do. Not an inconsistency: the first three payloads carry
 *     no `checklistTitle` and the last four do. Adding the slot would force a
 *     live-row join for a hard-deleted parent and produce a placeholder in the
 *     commonest case.
 *   - **`due.date_changed.reminder` names no offset and `cover.set` names no
 *     cover.** Both would need formatting rules (a minutes-before offset as
 *     prose; a colour, photo or attachment as a noun) that belong to the client
 *     rendering the entry, not to a sentence the server has to fix in English.
 *   - **`card.field_overridden` names the field and neither the overridden TEXT
 *     nor the member whose edit it displaced**, although its payload carries
 *     both (`overriddenValue`, `overriddenActorUserId` — B31, and the text is
 *     that block's one named exception to B14's "lengths, never the text"
 *     rule). The text because a description is a document rather than a noun
 *     phrase: quoting up to 16,384 characters inside a one-line sentence is the
 *     formatting decision the two bullets above hand to the client, and
 *     `FR-EXP-031` AC4 asks for the overridden version to be **retrievable**,
 *     which the payload satisfies. The member because the overridden actor can
 *     be **the actor themselves** — one person editing the same card in two
 *     tabs is the commonest way to produce one of these rows — so "overrode
 *     {member}'s unseen change" would render "Ada overrode Ada's unseen
 *     change" often enough to read as a bug, and naming them would also mean
 *     adding a key to `ACTIVITY_PAYLOAD_ID_KEYS`, which is name-keyed and
 *     would make `hydrate.ts` fetch it for every row in the product that
 *     happens to share the key.
 */
export const ACTIVITY_TEMPLATES: Record<ActivityTemplateKey, string> = {
  // Board — FR-BOARD-001, FR-BOARD-003, FR-EXP-012, FR-PERM-012,
  // FR-COLLAB-014, FR-TMPL-001, FR-TMPL-004, FR-TMPL-007, FR-PERM-019,
  // FR-PERM-020, FR-EXP-025.
  'board.created': 'created this board',
  'board.renamed': 'renamed this board (from {previousTitle})',
  'board.closed': 'closed this board',
  'board.reopened': 'reopened this board',
  'board.visibility_changed': 'changed the visibility of this board to {visibility}',
  'board.permission_changed': 'changed {setting} on this board to {value}',
  'board.background_changed': 'changed the background of this board',
  // Owner-directed card keys (`docs/notes/card-keys.md`): `{value}` is the new
  // key, verbatim — the reference prefix every card on the board now carries.
  'board.key_changed': 'changed the card key of this board to {value}',
  // ⚠ B36 — no slot on any of the three. The payload carries only
  // `{ workspaceId, title }` (see `packages/shared/src/events.ts`), and rule
  // 2 below already says "this board", never the board's own name, so there
  // is nothing left for a slot to name. `board.template_description_saved`'s
  // verb matches its identifier's own — `FR-TMPL-004` AC1's "write and SAVE
  // a description" is the requirement's own word, and the event name had to
  // be built from it anyway to fit `varchar(32)` (see that entry in
  // `./events`) — so sentence and stored `type` do not disagree.
  'board.template_marked': 'marked this board as a template',
  'board.template_unmarked': 'removed the template flag from this board',
  'board.template_description_saved': 'saved the template description on this board',
  'board.template_category_changed': 'changed the template category on this board',
  // ⚠⚠ B38, FR-PERM-019 / FR-PERM-020 — and **NO SLOT ON EITHER, LEAST OF ALL
  // ONE THAT WOULD RENDER THE TOKEN.** The payloads are
  // `{ workspaceId, inviteLinkId, roleGranted }` and
  // `{ workspaceId, inviteLinkId }` (see `./events`), and the token is absent
  // from both by rule: an append-only feed every board member reads is exactly
  // where a live credential must not be, because it would outlive its own
  // revocation. A `{token}` slot would put it in the rendered sentence, which
  // is the same disclosure one layer nearer the reader — do not add one, and
  // do not add a payload key to feed one.
  //
  // ⚠ `board.invite_link_created` names no ROLE either, though its payload
  // carries `roleGranted`. This is a stronger version of B36's slot-free
  // argument rather than the same one: the invite-link **row survives
  // revocation** (`is_active = false`, never a `DELETE` — `FR-PERM-020` AC4),
  // so `payload.inviteLinkId` resolves to a live row that still states its own
  // `role_granted`, and a slot here would be a second, append-only copy of a
  // fact the database still holds. Contrast `attachment.removed`, which carries
  // its whole subject precisely because the row is hard-deleted and nothing
  // afterwards says what it was.
  //
  // ⚠ The definite article on the second sentence is deliberate: FR-PERM-019
  // fixes **one active link per board at a time**, so "the invite link" is
  // always the one the reader means. Regenerating emits a revoked row followed
  // by a created row, which reads correctly in that order and would read as a
  // contradiction in any other.
  'board.invite_link_created': 'created an invite link for this board',
  'board.invite_link_revoked': 'revoked the invite link for this board',

  // Dashboard — FR-VIEW-027. No slots: the payload carries the tile id and
  // compact facet/chart values, but the user-visible fact is that the shared
  // board dashboard changed.
  'dashboard.tile_created': 'added a dashboard tile to this board',
  'dashboard.tile_changed': 'changed a dashboard tile on this board',
  'dashboard.tile_deleted': 'removed a dashboard tile from this board',

  // Card location — B54. No slots: the location snapshot rides in the payload
  // for clients that need detail, while the feed sentence names only the field.
  'card.location_set': 'set this card location',
  'card.location_changed': 'changed this card location',
  'card.location_removed': 'removed this card location',

  'sticker.placed': 'placed a sticker on this card',
  'sticker.moved': 'moved a sticker on this card',
  'sticker.removed': 'removed a sticker from this card',

  // Lists — FR-BOARD-009 to FR-BOARD-011, FR-BOARD-015, FR-TMPL-022.
  'list.created': 'added {list} to this board',
  'list.renamed': 'renamed {list} (from {previousTitle})',
  'list.recoloured': 'recoloured {list} to {colour}',
  'list.archived': 'archived {list}',
  'list.unarchived': 'restored {list} from the archive',
  'list.position_changed': 'moved {list}',
  'list.moved_between_boards.departed': 'moved {list} to another board',
  'list.moved_between_boards.arrived': 'moved {list} to {board}',

  // Cards — FR-BOARD-020 to FR-BOARD-028, FR-CARD-003, FR-NOTIF-018,
  // FR-AUTO-030 / FR-TMPL-030 / FR-NOTIF-035,
  // FR-TMPL-013, FR-TMPL-018, FR-CARD-035, and B37's card templates (FR-TMPL-008,
  // FR-TMPL-009, FR-TMPL-012).
  'card.created': 'added this card to {list}',
  'card.created_by_email': 'sent this card to the board',
  'card.added_to_list': 'filed this card to {list}',
  'card.renamed': 'renamed this card (from {previousTitle})',
  'card.position_changed': 'moved this card',
  'card.moved_between_lists': 'moved this card from {previousList} to {list}',
  'card.moved_between_boards.departed': 'moved this card to another board',
  'card.moved_between_boards.arrived': 'moved this card to {board}',
  'card.archived': 'archived this card',
  'card.unarchived': 'restored this card from the archive',
  'card.description_edited.edited': 'edited the description',
  'card.description_edited.cleared': 'removed the description',
  // ⚠ B31, FR-EXP-031 AC4 / FR-COLLAB-026 AC2 — the concurrency notice, and
  // the one card template whose slot is a FIELD NAME rather than a row or a
  // value a member typed. `{field}` is the payload's own `field` key, whose
  // domain is `CONCURRENCY_CONTROLLED_CARD_FIELDS` ('title' | 'description'),
  // so the words that land in the slot are exactly the words the sentence
  // wants and no mapping is needed. "unseen" is what distinguishes this from
  // every ordinary edit: the writer held a version of the field that somebody
  // had already replaced. It is NOT a split type — see `phrase.ts`, which
  // argues why a `.title` / `.description` pair would be worse.
  'card.field_overridden': 'overrode an unseen change to the {field}',
  // ⚠ B35, FR-TMPL-013 — and the slot is NOT optional decoration.
  // `FR-COLLAB-017` AC3 asks that an entry "records the source it was copied
  // from", and that requirement's own edge case says the criterion "cannot be
  // satisfied until copying a card is itself a product capability
  // (FR-TMPL-013) that records what it did". B35 is that capability, so a
  // slot-free "copied this card from another card" would ship the block that
  // was meant to discharge AC3 without discharging it. `card.relation_added`
  // above is the direct precedent for naming another card in a sentence.
  //
  // ⚠ The DESTINATION board is the row's own `board_id`, and the sentence
  // names neither it nor the source's board. This is not B21's deliberate
  // withholding — a copy writes one row on the destination and leaves the
  // source untouched, so there is no second audience to protect. The
  // *source card* may nevertheless be unreadable to whoever reads this
  // entry, which `phrase.ts` handles at the token layer: `hydrate.ts`'s card
  // lookup is viewer-scoped, so an unreadable source falls to the payload's
  // `sourceCardTitle` snapshot with `navigable: false` — `FR-COLLAB-018` AC3's
  // shape exactly, and the reason the payload carries that snapshot at all.
  'card.copied_from_card': 'copied this card from {card}',
  // B63, FR-CARD-035. The consolidated card is "this card"; the slot names
  // the two archived sources from the payload snapshot. The source rows may
  // remain archived or later be restored/renamed by undo, so the sentence must
  // not require a live lookup to stay intelligible.
  'card.merged': 'merged {cards} into this card',
  // ⚠ B37, FR-TMPL-008 / FR-TMPL-012 — the mark/unmark pair, and NO SLOT on
  // either. The payload is `{ workspaceId, listId, title }` (see
  // `./events`), and rule 2 above already says "this card", never the card's
  // own name, so there is nothing left for a slot to name: the title in the
  // payload is the card's own, which the board feed already renders beside
  // the sentence as `activityEntrySchema.card` and the card feed does not
  // need at all. `board.template_marked` and `board.template_unmarked` are
  // slot-free one container up for the identical reason, and the verbs are
  // deliberately the same pair of words so that a member reading both feeds
  // is reading one vocabulary.
  'card.template_marked': 'marked this card as a template',
  'card.template_unmarked': 'removed the template flag from this card',
  'card.template_hidden_from_list': 'hid this template card from its list',
  'card.template_unhidden_from_list': 'showed this template card in its list',
  // ⚠⚠ B37, FR-TMPL-009 — and the slot is the whole reason this is a name of
  // its own rather than a payload key on `card.copied_from_card` above (see
  // that name's neighbour in `./events` for the full argument). The two
  // sentences must differ: a template is not a source a card was duplicated
  // from, and "copied this card from {card}" is false of an instantiation
  // that `FR-TMPL-009` calls creating a card and that leaves the template
  // untouched. The slot resolves from `payload.templateCardId` through
  // `hydrate.ts`'s viewer-scoped card lookup, falling back to the payload's
  // `templateCardTitle` snapshot with `navigable: false` when the template
  // has been renamed, deleted or is on a board this reader cannot see —
  // `card.copied_from_card`'s own mechanism, reached by the ordinary path.
  //
  // ⚠ "the template" is in the sentence rather than left to the slot alone,
  // where `card.copied_from_card` says only "from {card}". The token itself
  // cannot say what kind of card it names — it renders a title — and a
  // reader seeing "created this card from Onboarding checklist" would have no
  // way to tell an instantiation from a copy, which is exactly the
  // distinction the second name was minted to keep.
  'card.created_from_template': 'created this card from the template {card}',
  'card.link_card_created': 'created this link card',
  'card.link_card_converted': 'converted this link card to an ordinary card',

  // Card members — FR-CARD-007, FR-CARD-008.
  'member.added': 'added {member} to this card',
  'member.removed': 'removed {member} from this card',
  'member.self_joined': 'joined this card',
  'member.self_left': 'left this card',

  // Votes — FR-AUTO-003, BE-68.8. No slot: the tally is derived state the
  // route reads from `card_votes`, and the payload carries no text.
  'card.voted_on': 'voted on this card',

  // PR-list approvals (0071). No slot: counts are derived state and the reason
  // text is never carried by an event.
  'card.approved': 'approved this card',
  'card.rejected': 'rejected this card',
  'card.approval_withdrawn': 'withdrew their approval verdict on this card',
  'card.approvals_met': 'got the approvals this card needs',
  'card.approvals_reset': 'reset every approval verdict on this card',
  'list.pr_list_changed': 'changed which list is the PR list',

  // Labels — FR-CARD-009 (the application), FR-CARD-010 (the board's set).
  'label.created': 'created the label {label}',
  'label.renamed': 'renamed the label {label} (from {previousName})',
  'label.recoloured': 'recoloured the label {label} to {colour}',
  'label.deleted': 'deleted the label {label}',
  'label.added': 'added the label {label} to this card',
  'label.removed': 'removed the label {label} from this card',

  // Comments — FR-COLLAB-001. One event of the three surfaces here; the entry
  // carries the comment itself on `activityEntrySchema.comment`, so the sentence
  // deliberately does not quote the body.
  'comment.added': 'commented on this card',

  // Dates and completion — FR-CARD-004, FR-CARD-013, FR-CARD-014.
  'due.date_set': 'set this card to be due {date}',
  'due.date_changed.date': 'changed the due date of this card to {date}',
  'due.date_changed.reminder': 'changed the due date reminder on this card',
  'due.date_removed': 'removed the due date from this card',
  'due.date_completed': 'marked this card complete',
  'due.date_uncompleted': 'marked this card incomplete',
  'start.date_set': 'set this card to start {date}',
  'start.date_removed': 'removed the start date from this card',

  // Covers — FR-CARD-011.
  'cover.set': 'set the cover on this card',
  'cover.removed': 'removed the cover from this card',

  // Checklists and their items — FR-CARD-016 to FR-CARD-019.
  'checklist.added': 'added the checklist {checklist} to this card',
  'checklist.renamed': 'renamed the checklist {checklist} (from {previousTitle})',
  'checklist.position_changed': 'moved the checklist {checklist}',
  'checklist.removed': 'removed the checklist {checklist} from this card',
  'checklist.item_added': 'added {item} to {checklist}',
  'checklist.item_edited': 'edited the checklist item {item}',
  'checklist.item_completed': 'completed {item} on {checklist}',
  'checklist.item_uncompleted': 'marked {item} incomplete on {checklist}',
  'checklist.item_position_changed': 'moved the checklist item {item}',
  'checklist.item_moved': 'moved {item} from {previousChecklist} to {checklist}',
  'checklist.item_removed': 'removed the checklist item {item}',
  'checklist.item_converted_to_card': 'converted {item} to {card}',
  'checklist.item_assigned': 'assigned {item}',
  'checklist.item_unassigned': 'unassigned {item}',
  'checklist.item_due_date_set': 'set a due date on {item}',
  'checklist.item_due_date_changed': 'changed the due date on {item}',
  'checklist.item_due_date_removed': 'removed the due date from {item}',

  // Attachments — FR-CARD-022, FR-COLLAB-005.
  'attachment.added': 'attached {attachment} to this card',
  'attachment.renamed':
    'renamed the attachment {attachment} (from {previousDisplayName})',
  'attachment.removed': 'removed the attachment {attachment} from this card',

  // Related cards — FR-TMPL-028.
  'card.relation_added': 'linked this card to {card}',
  'card.relation_removed': 'unlinked this card from {card}',

  // Mirrors — FR-TMPL-024 through FR-TMPL-026.
  'card.mirror_created': 'mirrored this card to this board',
  'card.mirror_removed': 'removed this card mirror from this board',
};

/**
 * Every template key, as a runtime tuple — **derived from the table above**, so
 * the two cannot drift.
 *
 * The cast is `EVENT_TYPE_NAMES`' own (`Object.keys` cannot know the key type),
 * and it is safe for the same reason: the object it reads is declared with a
 * total, exhaustive `Record` type one screen up. Writing the array out by hand
 * instead would let a key exist with no template, or a template with no key,
 * and `z.enum` below would happily accept the wrong set.
 */
export const ACTIVITY_TEMPLATE_KEYS = Object.keys(ACTIVITY_TEMPLATES) as [
  ActivityTemplateKey,
  ...ActivityTemplateKey[],
];

/** Matches one `{slot}` placeholder. Slot names are lower camel case, ASCII. */
const TEMPLATE_SLOT_PATTERN = /\{([A-Za-z][A-Za-z0-9]*)\}/g;

/**
 * The slot names one template asks for, in order of first appearance, without
 * duplicates.
 *
 * ⚠ **Derived from `ACTIVITY_TEMPLATES`, so it is a convenience and NOT the
 * contract.** A test that checked `phrase.ts` against this function would be
 * checking the table against itself. The contract is the explicit
 * slot-per-key map in `activity.test.ts`, which is written out by hand for
 * exactly that reason; this helper exists so `phrase.ts`' own spec can enumerate
 * what it must produce without re-parsing the table in two places.
 */
export function activityTemplateSlots(key: ActivityTemplateKey): readonly string[] {
  const seen: string[] = [];
  for (const match of ACTIVITY_TEMPLATES[key].matchAll(TEMPLATE_SLOT_PATTERN)) {
    // The `!` is safe because `TEMPLATE_SLOT_PATTERN`'s single group is not
    // optional: a match implies group 1 matched. `noUncheckedIndexedAccess`
    // cannot see that, and the assertion is the style `non-nullable-type-
    // assertion-style` enforces here. Making the group optional — `(…)?` — is
    // what would break it, and would break it silently: `undefined` would be
    // pushed into `seen` and every slot-agreement test would compare against a
    // slot named "undefined" rather than failing on the regex.
    const name = match[1]!;
    if (!seen.includes(name)) seen.push(name);
  }
  return seen;
}

/**
 * Render one entry's sentence in English, by substituting each `{slot}` with its
 * token's `text`.
 *
 * ⚠ **This is a TEST AND PLAIN-TEXT helper, not a response path.** See the
 * header, (b): the wire carries `templateKey` + `tokens` precisely so that the
 * sentence is assembled where it can be translated, and a `sentence` field added
 * to `activityEntrySchema` would be `FR-COLLAB-018`'s forbidden concatenation
 * with a longer name.
 *
 * ⚠ **It throws on a slot with no token, and the alternative was rejected
 * deliberately.** Substituting `''` produces "added this card to " — a sentence
 * that is grammatical enough to read past, valid enough to serialise, and wrong.
 * A wrong template ships that way and nothing anywhere reddens; the failure has
 * to be loud at the one place both halves are visible at once.
 *
 * ⚠ **A plain `Error`, deliberately NOT `ApiError('validation_failed')`** — the
 * opposite call from `decodeActivityCursor` three functions down, and the
 * distinction is *who supplied the bad input*. A cursor comes from a client, so
 * refusing it is a 422 naming the field. A missing token is produced only by
 * this server's own `phrase.ts` or by a test, so an `ApiError` would render a
 * server-side grammar bug as a 422 blaming the caller — a status code that
 * points a reader at the wrong half of the system.
 *
 * Extra tokens are ignored rather than refused: `phrase.ts` may legitimately
 * build a token the English template does not use but a translation does.
 *
 * ⚠ **The parameter is structural rather than `ActivityEntry`, and the index
 * signature on the token is what makes both callers work.** A whole parsed
 * entry passes as it stands (this reads only `templateKey` and `tokens`), and a
 * test may hand over `{ text: 'Doing' }` without inventing a `kind`, an `id` and
 * a `navigable` the function never looks at — while a full `ActivityToken`
 * literal is still accepted rather than tripping TypeScript's excess-property
 * check on a narrower `{ text: string }`.
 */
export function renderActivitySentence(entry: {
  templateKey: ActivityTemplateKey;
  tokens: Readonly<
    Record<string, { readonly text: string; readonly [extra: string]: unknown }>
  >;
}): string {
  return ACTIVITY_TEMPLATES[entry.templateKey].replace(
    TEMPLATE_SLOT_PATTERN,
    (_match, slot: string) => {
      const token = entry.tokens[slot];
      if (token === undefined) {
        throw new Error(
          `activity template "${entry.templateKey}" needs a token for {${slot}}`,
        );
      }
      return token.text;
    },
  );
}

/**
 * **The `via` slot — a SIBLING of `ACTIVITY_TEMPLATES`, deliberately not a
 * member of it.** One slot each, composed *around* an already-rendered
 * sentence when a connected app acted on a member's behalf (`FR-AUTO-033`:
 * the action is *"attributed to me and marked as performed by the named
 * tool"*). **The 72 event templates are untouched, and that is forced rather
 * than tasteful.**
 *
 * ⚠⚠ **Why this cannot be an edit to the 72, and why it cannot be a 73rd key.**
 *
 *   1. `renderActivitySentence` **throws** on a slot with no token, by design
 *      (see its docstring, and `FR-COLLAB-018.spec.ts`, which asserts the
 *      throw does not happen for any real phrasing). Appending `{via}` to even
 *      one of the 72 strings therefore breaks **every** render of that key
 *      with no connected app in sight — `apps/web`'s card back, at runtime,
 *      with nothing red at build time.
 *   2. `ACTIVITY_TEMPLATES` is `Record<ActivityTemplateKey, string>` and
 *      `ActivityTemplateKey` is derived from the **feed event names**, so
 *      there is no key to add: a 73rd entry is a type error, and widening the
 *      key union would demand a phraser and an event name that do not exist.
 *
 * One new translatable string, zero risk to the existing 72.
 *
 * ⚠ **This is a composition helper, not a display rule.** *When* a renderer
 * calls it — which is to say, how a card back shows a member and their tool at
 * once — is `FE-49.3`'s, not this file's. `apps/web/src/lib/activity.ts`'s
 * `activityActorName` still returns the actor's name whenever `actor !== null`
 * and falls back to `actorAutomationName` only when it is `null`, so with both
 * columns set the tool is **not** displayed today. That is a known gap, owned
 * by `FE-49.3`, and it is why this export currently has no caller in `apps/web`.
 */
export const ACTIVITY_VIA_TEMPLATE = '{sentence}, via {tool}';

/**
 * `renderActivitySentence(entry)`, wrapped in `ACTIVITY_VIA_TEMPLATE` under the
 * name of the tool that performed it.
 *
 * ⚠ **A plain-text and test helper, exactly like the function it wraps.** There
 * is still no `sentence` field on `activityEntrySchema`, and adding one would
 * be `FR-COLLAB-018`'s forbidden concatenation with a longer name. The wire
 * carries `templateKey`, `tokens` and `actorAutomationName` separately so that
 * the join happens where it can be translated.
 *
 * ⚠ **It throws on a blank `automationName`, for the reason its sibling throws
 * on a missing token.** *"archived this card, via "* is grammatical enough to
 * read past, valid enough to serialise, and wrong. A caller that does not know
 * whether a tool acted should test `entry.actorAutomationName` rather than
 * hand this function an empty string. A throw from the inner
 * `renderActivitySentence` propagates unchanged.
 *
 * ⚠ **The substitution is ONE pass over the outer template, through a callback,
 * and both halves of that matter.** A callback replacement is inserted
 * verbatim, so a card titled `$&` or `$'` inside the rendered sentence cannot
 * reach `String.replace`'s pattern syntax; and one pass over
 * `ACTIVITY_VIA_TEMPLATE` means the substituted sentence is never rescanned, so
 * a card titled `{tool}` cannot become the tool's name. Two chained
 * `.replace('{sentence}', …)` calls fail both ways, silently, on data a member
 * chose.
 */
export function renderActivitySentenceVia(
  entry: {
    templateKey: ActivityTemplateKey;
    tokens: Readonly<
      Record<string, { readonly text: string; readonly [extra: string]: unknown }>
    >;
  },
  automationName: string,
): string {
  if (automationName.trim() === '') {
    throw new Error('activity via template needs a non-blank tool name for {tool}');
  }

  const sentence = renderActivitySentence(entry);

  return ACTIVITY_VIA_TEMPLATE.replace(TEMPLATE_SLOT_PATTERN, (_match, slot: string) => {
    if (slot === 'sentence') return sentence;
    if (slot === 'tool') return automationName;
    // Unreachable while the constant above is the two slots it declares, and
    // loud rather than silent if someone adds a third.
    throw new Error(`activity via template needs a token for {${slot}}`);
  });
}

// ---------------------------------------------------------------------------
// Bounds — a request shape and a product decision, no column widths.
// ---------------------------------------------------------------------------

/**
 * How many entries a feed returns when the caller sends no `?limit`.
 *
 * `./comment`'s `COMMENT_PAGE_SIZE_DEFAULT` verbatim, and for the same reason:
 * one round trip renders the section on the overwhelming majority of cards,
 * and a default that is smaller than the ceiling keeps it a *page* rather than
 * a de-facto "everything".
 *
 * ⚠ Tests must pass `?limit=` explicitly. A fixture that leans on the default
 * asserts this constant rather than the behaviour it means to check.
 */
export const ACTIVITY_PAGE_SIZE_DEFAULT = 25;

/**
 * The ceiling on `?limit`.
 *
 * ⚠ **`activity_entries` is the largest unbounded collection in the product**
 * — larger than comments, because every mutation twenty-two blocks make writes
 * to it and nothing ever deletes a row (see `activityFeedSchema` on retention).
 * Without a ceiling, `?limit=1000000` is a legal request that reads a board's
 * entire history into memory, hydrates every id it references, and serialises
 * the lot.
 */
export const ACTIVITY_PAGE_SIZE_MAX = 100;

/**
 * `FR-COLLAB-017`'s edge case — *"Rapid repeated changes by the same actor …
 * should collapse into one entry within a short window …; the exact window is a
 * product decision"* — **answered: five minutes**, recorded in
 * `docs/requirements/03-collaboration.md` rather than only here.
 *
 * ⚠ **The window is only one of four conditions, and it is the least
 * interesting of them.** `apps/server/src/activity/collapse.ts` collapses a
 * **consecutive** run of entries by the **same actor** on the **same card**,
 * within this window, **whose rendered sentence is byte-identical**. Byte
 * identity is what makes the collapse safe: the surviving sentence is by
 * construction true of every member of the run, so a collapse can never
 * falsify anything. Three quick drags collapse (`card.position_changed` names
 * no position); an A→B→C run of `card.moved_between_lists` renders three
 * different sentences and correctly refuses to.
 *
 * ⚠ **There is no maintained list of collapsible event types and none should be
 * added.** A curated list encodes exactly the judgement byte-identity makes for
 * free, and it goes stale the first time a template changes.
 */
export const ACTIVITY_COLLAPSE_WINDOW_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// INPUT — what the two routes ACCEPT.
// ---------------------------------------------------------------------------

/**
 * `FR-COLLAB-020`'s two-way filter, shared by both feeds.
 *
 * ⚠ **`'comments'` means the `comment.added` **event**, not the `comments`
 * table.** The card feed is the merged stream (see `activityEntrySchema.comment`),
 * so filtering to comments narrows the same query rather than switching to
 * B16's route — which is what makes `FR-COLLAB-020` AC3's "older entries are
 * appended **without losing my filter selection**" a property of one cursor
 * rather than of two streams a client would have to reconcile.
 *
 * ⚠ **Two values, not three, and `FR-COLLAB-019`'s toggle is the same
 * parameter.** That requirement's "Show details" / "Hide details" is a *client*
 * control whose two states are exactly these two values; its persistence
 * (`user_preferences.hide_activity_details`, a column that has existed since
 * `0001` and that **no route writes**) is `FE-28.1`'s and is not B28's. B28's
 * contribution is that the toggle is expressible at all.
 */
export const ACTIVITY_FILTERS = ['all', 'comments'] as const;

export const activityFilterSchema = z.enum(ACTIVITY_FILTERS);

export type ActivityFilter = z.infer<typeof activityFilterSchema>;

/**
 * An opaque paging cursor. Bounded, not structured — a client reads it from
 * `activityFeedSchema.nextCursor` and hands it back verbatim.
 *
 * 128 is `./comment`'s, `./notification`'s and `./board-archive`'s bound
 * unchanged: the raw string is `` `${createdAt}|${id}` ``, a Postgres timestamp
 * of at most 26 characters plus a separator plus a 26-character ULID — 53 bytes,
 * 71 base64url characters — so there is comfortable margin, and a fabricated
 * cursor still cannot push a megabyte through a query string.
 *
 * ⚠ **Bounded here and *decoded* separately.** This schema only says "a
 * plausible cursor-shaped string"; `decodeActivityCursor` is what refuses a
 * malformed one, and it must be called — base64 decoding does not fail on
 * garbage.
 */
export const activityCursorSchema = z.string().min(1).max(128);

/**
 * The querystring of `GET /cards/:id/activity` and `GET /boards/:id/activity` —
 * one schema for both, because the two routes take the same three parameters and
 * differ only in what they are scoped to.
 *
 * ⚠ **`z.strictObject`, and the key it has to refuse is a real one.**
 * `GET /boards/:id/archive` (B22) — one route over, on the same `/boards/:id/`
 * prefix — takes `?type=cards|lists`. A caller who sends `?type=` here gets a
 * **422 naming the key** rather than a 200 that silently served the unfiltered
 * feed, which is the difference between a client bug that surfaces immediately
 * and one that surfaces as "the filter does nothing".
 *
 * ⚠ **`z.coerce.number()`'s ordering was measured by `./comment` and is reused
 * rather than re-measured.** Under Zod 4 the coercion runs **before** `.int()`,
 * `.min()` and `.max()`, so the bounds apply to the number and not to the
 * string's length, and `.default()` is evaluated before the coercion so an
 * omitted `?limit` yields `ACTIVITY_PAGE_SIZE_DEFAULT` rather than
 * `Number(undefined)`, which is `NaN`. `?limit=` (empty) is `too_small`, because
 * `Number('')` is `0` and not a type error. `comment.test.ts` pins all six
 * outcomes; this file pins the ones a caller of *this* route sees.
 */
export const activityQuerySchema = z.strictObject({
  filter: activityFilterSchema
    .default('all')
    .describe(
      'Which entries to return: "all" (default) for the whole feed, ' +
        '"comments" for comment entries only — FR-COLLAB-020 AC2, and the ' +
        'server half of FR-COLLAB-019’s Show details / Hide details toggle.',
    ),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(ACTIVITY_PAGE_SIZE_MAX)
    .default(ACTIVITY_PAGE_SIZE_DEFAULT)
    .describe(
      'How many entries to return, 1 to 100, default 25. A page may come back ' +
        'SHORTER than this without being the last page — collapsing and the ' +
        'orphaned-comment drop both happen after the LIMIT — so a client must ' +
        'read nextCursor rather than compare lengths.',
    ),
  cursor: activityCursorSchema
    .optional()
    .describe(
      'An opaque cursor from a previous response’s nextCursor. Omit it for the ' +
        'first (newest) page. A malformed cursor is a 422, never a silent ' +
        'restart from the top.',
    ),
});

export type ActivityQuery = z.infer<typeof activityQuerySchema>;

/**
 * The querystring of `GET /workspaces/:id/activity`.
 *
 * This is a sibling of `activityQuerySchema`, not a widening of it: card and
 * board activity routes must continue to refuse `?memberId=`, while the
 * workspace route needs it for `FR-COLLAB-021`'s selected-member view.
 */
export const workspaceActivityQuerySchema = z.strictObject({
  ...activityQuerySchema.shape,
  memberId: ulidSchema
    .optional()
    .describe(
      'When present, return only entries whose actor_user_id is this workspace member.',
    ),
});

export type WorkspaceActivityQuery = z.infer<typeof workspaceActivityQuerySchema>;

// ---------------------------------------------------------------------------
// The cursor codec — a FOURTH copy. See the header, (c), before extracting it.
// ---------------------------------------------------------------------------

/**
 * How Postgres renders a `timestamptz`, permissively — copied from
 * `./notification`, itself the fix for the hole `./comment`'s original codec
 * left open. This is the **fourth** copy, and it is module-private in all four.
 *
 * ⚠ **Without it, `decodeActivityCursor` is a 500 generator rather than a 422
 * generator.** `'notatime'` is legal base64url content, decodes cleanly, splits
 * on `|` into two parts, and passes a bare `z.string()` — so the route builds
 * `WHERE (created_at, id) < ('notatime'::timestamptz, …)` and Postgres raises
 * `invalid input syntax for type timestamp with time zone`. `./notification`
 * measured this and recorded it as the one way its codec deliberately diverges
 * from `./comment`'s.
 *
 * The fractional part is optional because `timestamptz(3)::text` omits it when
 * it is zero — a property of Postgres rather than of any one column, which is
 * why this is reused rather than re-measured.
 */
const POSTGRES_TIMESTAMPTZ =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d{1,6})?([+-]\d{2}(:\d{2}(:\d{2})?)?|Z)?$/;

/**
 * The decoded cursor: the exact `(created_at, id)` pair of the last entry on the
 * page that was served.
 *
 * Module-private on `commentCursorPayloadSchema`'s, `notificationCursorPayload
 * Schema`'s and `archiveCursorPayloadSchema`'s precedent — a caller receives this
 * shape from `decodeActivityCursor` and never constructs one from a cursor by
 * hand.
 */
const activityCursorPayloadSchema = z.strictObject({
  createdAt: z.string().min(1).max(64).regex(POSTGRES_TIMESTAMPTZ),
  id: ulidSchema,
});

/**
 * The base64url alphabet, unpadded — a fourth module-private copy.
 *
 * ⚠ **Hand-rolled rather than `btoa`/`atob` or `Buffer`, both rejected for
 * measured reasons rather than taste**, as `./comment` records: `Buffer` is
 * Node-only and this package is bundled into `apps/web` by Vite, which does not
 * polyfill it; `btoa`/`atob` exist at runtime everywhere but are **not
 * declared** under this workspace's `lib: ["ES2023"]` with no DOM lib, so `tsc`
 * answers `TS2304: Cannot find name 'btoa'`.
 *
 * Do not export it and do not import one of the other three.
 */
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Latin-1 (here: ASCII) in, unpadded base64url out. */
function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

/**
 * Unpadded base64url in, latin-1 out. Throws on a character outside the alphabet
 * and on a length no base64 string can have (`length % 4 === 1`).
 *
 * ⚠ It does **not** throw on every string that merely fails to mean anything —
 * `'abc'` decodes to two bytes of noise quite happily — which is precisely why
 * `decodeActivityCursor` validates the *shape* afterwards rather than treating a
 * successful decode as proof of anything.
 */
function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    // Masked to sixteen bits deliberately — `./comment`'s note: only the low
    // twelve are ever read back, and an unmasked accumulator would run past
    // thirty-two bits, where JavaScript's bitwise operators truncate.
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

/**
 * Turn the last entry on a page into the cursor that fetches the next one.
 *
 * ⚠ **VALUE-CARRYING and self-contained — base64url of
 * `` `${createdAt}|${id}` `` — and emphatically NOT `?before=<entryId>`.**
 * `./comment`'s two mechanical reasons both apply here, and one of them applies
 * *more* strongly: this function takes **no database handle**, which is the
 * property rather than an accident, and an activity feed pages over a table
 * whose rows are dropped from the response at render time (an orphaned
 * `comment.added` whose comment was hard-deleted, `FR-COLLAB-003`), so a
 * row-naming cursor could name an entry the next page will not return.
 *
 * ⚠⚠ **TIES ON `created_at` ARE GUARANTEED HERE RATHER THAN RARE, WHICH IS WHY
 * THE SECOND COMPONENT IS NOT OPTIONAL.** `insertEvents` never supplies
 * `created_at`, so it defaults to `now()` — the **transaction** timestamp — and
 * every event of one gesture therefore shares it to the millisecond. A
 * cross-board move writes two rows at the identical instant; a checklist copy
 * writes one per item. `ORDER BY created_at DESC, id DESC` with a
 * `(created_at, id)` row-comparison keyset is the only total order available,
 * and a cursor carrying the timestamp alone would skip or repeat the rest of a
 * tie group with a 200 and a well-formed body.
 *
 * ⚠ **`createdAt` is the exact string the driver returned**, in Postgres'
 * spelling (`2026-08-12 12:00:00.123+00`, a space rather than a `T`) rather than
 * an ISO one — which is why the route's `WHERE` casts it
 * (`$cursorCreatedAt::timestamptz`) and why `POSTGRES_TIMESTAMPTZ` above accepts
 * both separators.
 *
 * ⚠ **Encode from the last row KEPT, never from the extra `limit + 1`th row**
 * fetched to discover whether another page exists — and, in this block, **never
 * from the last row *rendered*** either. Collapsing and the orphaned-comment
 * drop both remove entries after the page is fetched, so the cursor must come
 * from the raw rows; taken from the rendered ones, every collapsed run at a page
 * boundary silently skips the entries behind it.
 */
export function encodeActivityCursor(row: { createdAt: string; id: string }): string {
  const raw = `${row.createdAt}|${row.id}`;
  // Guard the one assumption the codec makes. Both halves are ASCII by
  // construction (a Postgres timestamp and a Crockford base32 ULID), so this can
  // only fire if a caller invents a row — in which case failing loudly here
  // beats emitting a cursor that decodes to mojibake.
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charCodeAt(i) > 0xff) {
      throw new Error('activity cursor components must be latin-1');
    }
  }
  return toBase64Url(raw);
}

/**
 * Turn a cursor back into the `(created_at, id)` pair it names.
 *
 * ⚠ **A MALFORMED CURSOR IS A 422, NEVER "START FROM THE TOP"** — `./comment`'s,
 * `./notification`'s and `./board-archive`'s argument verbatim: a silent
 * fallback to page one turns "Load more activity" into an infinite loop that
 * re-serves the newest page for ever, with a 200 and a well-formed body, so
 * nothing anywhere fails. That is why this throws an
 * `ApiError('validation_failed')` rather than a plain `Error` a route must
 * remember to catch — the rejected alternative leaves the refusal a matter of
 * handler discipline, and a forgotten `try` is a **500** instead of the 422.
 *
 * ⚠ Note the deliberate asymmetry with `renderActivitySentence` above, which
 * throws a **plain** `Error`: a cursor is client input and a missing token is
 * not.
 *
 * ⚠ **The shape check after the decode is the whole defence, because base64
 * decoding does not fail on garbage.** `'abc'` is legal base64url and decodes to
 * two bytes of noise. All **five** of these are refusals:
 *
 *   1. a character outside the base64url alphabet;
 *   2. an impossible length (`length % 4 === 1`);
 *   3. a decoded string that does not split into **exactly two** parts on `|`;
 *   4. a second part that is not a ULID;
 *   5. ⚠ a first part that does not look like a Postgres `timestamptz` —
 *      `./comment`'s four stop at (4), and the hole that leaves is measured:
 *      `'notatime'` reaches `'notatime'::timestamptz` and turns this 422 into a
 *      **500**. See `POSTGRES_TIMESTAMPTZ`.
 *
 * `|` is safe as the separator because neither component can contain one — a
 * `timestamptz` renders as digits, hyphens, colons, a dot, a space and a sign,
 * and Crockford base32 is `[0-9A-HJKMNP-TV-Z]`.
 */
export function decodeActivityCursor(cursor: string): { createdAt: string; id: string } {
  const invalid = (): never => {
    throw new ApiError('validation_failed', 'Malformed cursor', {
      cursor: ['must be a cursor returned by a previous page of this feed'],
    });
  };

  let raw: string;
  try {
    raw = fromBase64Url(cursor);
  } catch {
    return invalid();
  }

  const parts = raw.split('|');
  if (parts.length !== 2) return invalid();

  const parsed = activityCursorPayloadSchema.safeParse({
    createdAt: parts[0],
    id: parts[1],
  });
  if (!parsed.success) return invalid();

  return parsed.data;
}

// ---------------------------------------------------------------------------
// VIEWS — what the two routes RETURN.
// ---------------------------------------------------------------------------

/**
 * What kind of thing a token names, so a client can choose an icon, a link
 * target and a fallback rendering without parsing the sentence.
 *
 * The vocabulary is fixed by the block's design note and is deliberately
 * *closed*: a token kind nothing produces is cheap, and a kind invented at a
 * call site is a client that cannot render it. Two are declared and unused by
 * any template today — `'comment'` (the comment itself rides on
 * `activityEntrySchema.comment`, so no sentence needs to name one) and
 * `'count'` (no template renders a number; `checklist.removed`'s item counts are
 * in the payload and deliberately not in the sentence). Both are kept because
 * the alternative is a later block widening the enum, which changes a shipped
 * response's vocabulary for a reason nobody can see from here.
 */
export const activityTokenKinds = [
  'list',
  'card',
  'board',
  'member',
  'label',
  'checklist',
  'checklist_item',
  'attachment',
  'comment',
  'text',
  'date',
  'count',
] as const;

export const activityTokenKindSchema = z.enum(activityTokenKinds);

export type ActivityTokenKind = z.infer<typeof activityTokenKindSchema>;

/**
 * One substitutable value in a sentence — `FR-COLLAB-018`'s "object names render
 * as links where the object is navigable".
 */
export const activityTokenSchema = z.object({
  kind: activityTokenKindSchema,
  /**
   * What the client prints. **Always populated — never empty, never an id.**
   *
   * ⚠ **Resolved in a fixed order, and the order is the requirement:** the live
   * row's current name first (AC2 wants the link to reach the object *as it is
   * now*, so a rename does not have to rewrite history); then the payload's
   * snapshot (`title`, `name`, `displayName`, `text`) when the row is gone —
   * `label.deleted`, `checklist.removed`, `card.deleted` and
   * `checklist.item_moved` all snapshot deliberately for this; then a neutral
   * placeholder.
   *
   * ⚠ **The placeholder branch is not a defect and must not be turned into a
   * dropped token or a hidden entry.** `FR-COLLAB-018` AC3 is explicit that an
   * entry naming an object that has since been archived or deleted "still
   * renders as plain text and **the entry is not hidden**". An empty `text` here
   * would render a sentence with a hole in it — see `renderActivitySentence`,
   * which throws rather than substitute one.
   */
  text: z.string(),
  /**
   * The row this token names, when one exists and is addressable — `null` for a
   * `'text'` or `'date'` token, and for an object whose row is gone.
   *
   * ⚠ **`null` and `navigable: false` are not the same fact and both are
   * needed.** A hard-deleted label has neither an id nor a destination; an
   * **archived** list has a perfectly good id that a client must not link to.
   */
  id: ulidSchema.nullable(),
  /**
   * Whether the client renders it as a link.
   *
   * ⚠ **`false` with `text` intact is the archived-or-deleted rendering** —
   * `FR-COLLAB-018` AC3 again. Deciding it client-side from `id !== null` looks
   * equivalent and is not: an archived list keeps its id.
   */
  navigable: z.boolean(),
});

export type ActivityToken = z.infer<typeof activityTokenSchema>;

/**
 * The card an entry belongs to — `FR-COLLAB-020` AC1, *"each entry naming the
 * card it belongs to"*, and AC4, *"when I click an activity entry's card name,
 * then the card back opens"*.
 *
 * ⚠ **Not `cardSchema` narrowed and not a token.** Not `cardSchema` because that
 * shape carries eleven fields a feed row has no use for and would make a page of
 * 25 entries carry 25 whole cards; not a token because it is not substituted
 * into a sentence — the sentence says "this card" (see `ACTIVITY_TEMPLATES`
 * rule 2) and this rides *beside* it, so that a board feed can label the entry
 * without the card's name appearing twice.
 *
 * ⚠ **`null` on a board-scoped entry** (`board.renamed`, `list.created`,
 * `label.created` — every entry whose `activity_entries.card_id` is `NULL`),
 * which is the ordinary state on the board feed rather than an edge case. That
 * is the whole of the rule the design fixes, and it is the one a client may rely
 * on.
 *
 * ⚠ **The CARD feed may additionally leave it null on every entry, and this
 * schema deliberately does not decide which.** There the card is the route's own
 * path segment, so repeating it on 25 rows is 25 copies of a fact the caller
 * supplied — the shape `./cross-board-move` refused for `direction`. Populating
 * it anyway is *also* defensible (one pipeline, one projection, one fewer branch
 * for the route to get wrong) and costs only bytes. **Whichever the route
 * chooses, a client must read `card === null` as "no card to name here" and
 * never as "this is the card feed"** — inferring the surface from the field is
 * how a shared component starts rendering the wrong header the day the other
 * choice is made.
 */
export const activityCardRefSchema = z.object({
  id: ulidSchema,
  /**
   * `cards.title varchar(512)` — bounded by `CARD_TITLE_MAX_LENGTH` from
   * `./board`, imported rather than restated because that constant already
   * describes this column (`./card`'s standing rule).
   */
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  /**
   * `cards.short_link` — what `cardPath()` in `./address` builds a URL from, so
   * AC4's click has somewhere to go without a second lookup.
   */
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /**
   * The card's reference (`MG-0001`) for **this reader**, or `null` — an inbox
   * card, a deleted one, or a reader outside the reference scope
   * (`isBoardReferenceableBy`). A client links `/c/<ref ?? shortLink>`, i.e.
   * `cardPath()` in `./address`. `docs/notes/card-keys.md`, "Wire".
   */
  ref: cardReferenceSchema.nullable(),
  /**
   * ⚠ **Always `true` today, and on the wire anyway.** The board feed's `WHERE`
   * already excludes entries whose card is soft-deleted (a deleted card resolves
   * to nothing, and AC4 makes this name a link), so nothing currently produces
   * `false`. It is declared because the *alternative* — a client inferring
   * navigability from the field's presence — breaks silently the day an
   * **archived** card is shown here, which `FR-EXP-010`'s "the archive is a
   * recovery surface" reading makes likely rather than hypothetical.
   */
  navigable: z.boolean(),
});

export type ActivityCardRef = z.infer<typeof activityCardRefSchema>;

/**
 * The board an entry belongs to on the workspace activity feed.
 *
 * Not `boardSchema` narrowed: a workspace feed row needs the board's label and
 * permalink, not its workspace id, visibility, slug or member list. The route's
 * permission predicate decides whether the viewer may see the row; this shape
 * only carries the already-authorised navigation target.
 */
export const activityBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /**
   * Deliberately mirrors `activityCardRefSchema`: callers must not infer
   * navigability from the field's presence, because archived/deleted handling is
   * a route decision rather than a client-side id check.
   */
  navigable: z.boolean(),
});

export type ActivityBoardRef = z.infer<typeof activityBoardRefSchema>;

/**
 * One entry, as both feeds return it.
 *
 * ⚠ **`type` is the WHOLE registry (`EVENT_TYPE_NAMES`), not `FEED_EVENT_TYPES`,
 * and that is deliberate as well as necessary.** Deliberate: this field records
 * *what was written*, and narrowing a view to the subset a query happens to
 * select would make the schema a second, silently drifting copy of the
 * `WHERE` — `templateKey` is the field that carries the feed's own vocabulary.
 * Necessary: `FEED_EVENT_TYPES` is a filtered array and therefore not a
 * non-empty tuple, so `z.enum` cannot take it.
 */
export const activityEntrySchema = z.object({
  /** `activity_entries.id` — also the second half of the paging cursor. */
  id: ulidSchema,
  type: z.enum(EVENT_TYPE_NAMES),
  /**
   * Which sentence to render. See the header, (a), for why this is not a second
   * encoding of `type`: four types have two keys each.
   */
  templateKey: z.enum(ACTIVITY_TEMPLATE_KEYS),
  /**
   * The values the template's `{slot}`s take, keyed by slot name.
   *
   * ⚠ **A record rather than an array, because a slot is addressed by name.** An
   * array would make the client's substitution positional, and a translation
   * that reorders the sentence — which is the entire point of translatable
   * templates — would then silently swap two names.
   *
   * `{}` is the ordinary state for the many templates that take no slot.
   */
  tokens: z.record(z.string(), activityTokenSchema),
  /**
   * Who did it, or `null`.
   *
   * ⚠ **`LEFT JOIN users` with NO join to `board_members`.** `FR-COLLAB-018`
   * AC4 is explicit: "given an actor has been **removed from the board**, when
   * their old entries render, then their name and avatar still resolve." An
   * INNER join to `board_members` would hide exactly the history the AC names,
   * and it would do it with a 200 and a shorter page.
   *
   * `publicProfileSchema` reused whole, exactly as `commentSchema.author` does,
   * so an actor cannot acquire an `email` by accident.
   */
  actor: publicProfileSchema.nullable(),
  /**
   * `activity_entries.actor_automation_name` — `FR-COLLAB-017`'s edge case:
   * actions by an automation are attributed "to that automation/integration by
   * name, **never to a blank actor**".
   *
   * ⚠ **Nothing emits it today and the field ships anyway.** The projection is
   * one column and the AC is free; the alternative is that the first automation
   * block has to widen a shipped response. A client must treat
   * `actor === null && actorAutomationName === null` as the third state (a
   * purged account) rather than as "nobody".
   */
  actorAutomationName: z.string().nullable(),
  /**
   * `activity_entries.created_at timestamptz(3)`.
   *
   * ⚠ **A bounded-below string and emphatically NOT `z.iso.datetime()`** —
   * `commentSchema.createdAt`'s note applies verbatim: `pg` hands a
   * `timestamptz` back in Postgres' own spelling (`2026-08-12 12:00:00.123+00`,
   * a **space** rather than a `T`, no `Z`), so an ISO check here is a **500 on
   * every read**.
   *
   * `FR-COLLAB-018` wants a *relative* timestamp ("2h ago") and `FR-COLLAB-006`
   * an absolute one on hover; both are rendered by the client from this one
   * value, and there is deliberately no second, pre-formatted field.
   */
  createdAt: z.string().min(1),
  /** `FR-COLLAB-020` AC1. See `activityCardRefSchema` for when this is `null`. */
  card: activityCardRefSchema.nullable(),
  /**
   * ⚠⚠ **The comment itself, on a `comment.added` entry — and `null` on every
   * other type. THIS FIELD IS WHY THE CARD FEED IS ONE STREAM RATHER THAN TWO.**
   *
   * `FR-COLLAB-017`'s Behaviour is verbatim: *"The card back keeps **one**
   * reverse-chronological stream **mixing comments and system-generated activity
   * entries**."* B16 serves comments from `comments`; `activity_entries` holds a
   * `comment.added` row for every one of them. **Two independently paged streams
   * cannot be merged by a client** — their cursors advance over different tables,
   * and a merge across page boundaries loses rows silently. So the feed is the
   * merged stream and this field carries the comment.
   *
   * ⚠⚠ **`commentStreamCommentSchema` — THE EXTENSION, NOT THE BASE — AND THAT
   * IS A DELIBERATE REPOINT, NOT A WIDENING.** `commentSchema` is untouched and
   * `commentStreamCommentSchema` is untouched; the *reference* moved from the
   * base to the copy `./comment` already ships. CLAUDE.md's binding rule is
   * *extend a shipped schema on a copy, never widen it in place* — the copy was
   * already made, by the chunk that added `authorPresence` and `via`, and this
   * field is now a **consumer** of it. Nothing is re-declared here: a second
   * spelling of `authorPresence` would be strictly worse than the clamp this
   * repoint removes.
   *
   * ⚠ **The "reused WHOLE, never restated" property survives, TRANSITIVELY.**
   * `commentStreamCommentSchema` is `commentSchema.extend({ … })`, so a field
   * added to a comment (B61's `reactions` array was the last) still appears
   * here at once, and this view still cannot drift into a second, narrower
   * comment. The anti-drift guarantee is preserved rather than traded away.
   *
   * ⚠ **Why the feed needs the two fields, superseding one sentence in
   * `./comment`.** `commentStreamSchema`'s docstring (`comment.ts:1301`) says
   * `FE-16.2` and `FE-70.4` *"must not put them on `activityEntrySchema.comment`
   * … these two fields are exactly what the stream needs and the feed does
   * not"*. **The second half is wrong on the facts**: the card back renders its
   * comments out of **this** feed, not out of the stream
   * (`CommentSection.tsx`), so `FR-COLLAB-006` AC4's deactivated-style avatar is
   * a *feed* obligation. Carrying it only on the stream forced a second, clamped
   * read at `COMMENT_PAGE_SIZE_MAX`, past which every comment rendered with no
   * presence at all. The **other** half of that docstring's argument stands
   * untouched and is why the base stays the base: a **required** field on
   * `commentSchema` would make `hydrate.ts`'s hand-built literals a `tsc` error
   * in three shipped activity routes. Both fields are `.optional()`, so this
   * repoint is a strict **superset** at the type level and at the parse level —
   * every value the base accepted still parses, and every construction site that
   * builds a `Comment` still compiles.
   *
   * ⚠ **Four routes serialise this entry, and none of them must withhold the
   * two fields.** `GET /cards/:id/activity` (`requireCardReader`),
   * `GET /boards/:id/activity` (`requireBoardReader`),
   * `GET /workspaces/:id/activity` (`isBoardReadableBy`) and
   * `GET /users/:id/profile-page` (`isBoardListableBy`, which is *stricter* —
   * it drops the public-board arm). All four are restricted in SQL to boards
   * the viewer may read, so `authorPresence` — a statement about the author's
   * membership of **that** board — reaches only an audience that can already
   * read the same fact from the board's member list and from the feed's own
   * `{member}` tokens. `via` is `activity_entries.actor_automation_name`, which
   * `actorAutomationName` two fields up **already ships unconditionally on every
   * entry**. Nothing new is disclosed to anyone.
   *
   * ⚠ **And that argument was re-run against the SECOND audience, the one a
   * session-shaped argument misses.** Two of the four —
   * `GET /cards/:id/activity` and `GET /boards/:id/activity` — are on
   * `apps/server/src/auth/connected-app-allowlist.ts` (rows 9 and 10), so a
   * B82 bearer grant reaches them; the other two answer 403 to a bearer
   * identity whatever its scope. The grant does not widen board access — the
   * route's own `requireCardReader`/`requireBoardReader` still runs against the
   * granting user — and, decisively, **`GET /cards/:id/comments` (row 8) and
   * `GET /boards/:id/members` (row 11) are allowlisted `read` rows too**. The
   * comment stream already serialises `commentStreamCommentSchema`, so a grant
   * holder receives these exact two fields, on these exact comments, today. The
   * bearer path gains nothing it could not already fetch in one more call.
   *
   * ⚠ **The orphan drop is the same LEFT JOIN that populates this field, which is
   * why building this and forgetting that is the likely failure rather than the
   * reverse.** `comments` is hard-deleted (`FR-COLLAB-003` forbids a tombstone)
   * and no foreign key points at it, so a `comment.added` entry outlives its
   * comment. Such an entry is **dropped from the page**, not served with `comment:
   * null` — rendering "commented on this card" for a comment that no longer exists
   * *is* the tombstone the requirement forbids, arriving by another route.
   */
  comment: commentStreamCommentSchema.nullable(),
  /**
   * ⚠⚠ **The override notice's own payload, on a `card.field_overridden` entry —
   * and `null` on every other type. THIS FIELD IS WHAT MAKES `FR-EXP-031` AC4
   * TRUE.**
   *
   * AC4 is verbatim: *"two members edited the same description while one was
   * offline → the later edit wins **and the overridden version is retrievable
   * from the card's activity**."* `.b31-design.md` §3.5 fixes where "the card's
   * activity" is — *"the **durable** contract is the `card.field_overridden`
   * activity entry, read through B28's `GET /cards/:id/activity`"* — and §3.2
   * fixes what carries the text: `cardFieldOverridePayloadSchema`'s
   * `overriddenValue`. ⚠ **Written into `activity_entries.payload` and not
   * projected here, the requirement is unmet with nothing red anywhere**: the
   * entry appears on the feed, its sentence is correct, and the one thing AC4
   * asks for — the overridden text — reaches no client. That is the exact shape
   * of failure this repository keeps recording, so the projection is a field of
   * the shipped entry rather than a second route.
   *
   * ⚠ **`comment` is the precedent, on this very schema**, and the two are the
   * same shape for the same reason: a template deliberately does not quote the
   * thing (`ACTIVITY_TEMPLATES['card.field_overridden']` is "overrode an unseen
   * change to the {field}" — see that table's own bullet for why the text and
   * the displaced member are both withheld from the *sentence*), so the thing
   * rides **beside** the sentence. It is emphatically **not** a token: a token
   * is substituted into English, and a 16,384-character description is not a
   * noun phrase.
   *
   * ⚠ **`cardFieldOverridePayloadSchema` reused WHOLE, never restated** —
   * `commentSchema`'s rule two fields up, so a key added to the payload appears
   * here at once and this view cannot drift into a second, narrower override.
   *
   * ⚠ **This widens a schema that serialises BOTH feeds, deliberately, and the
   * "extend on a copy" convention does not bite here.** That convention exists
   * because widening changes responses that must not carry the field; B21's
   * payload rule is what settles whether the *board* feed may carry this one,
   * and §3.2 already answers it — *no key here names a row the audience of this
   * row's own `board_id` could not already see; the overridden value is the
   * card's own former text, readable by every member of that board.* Both feeds
   * are scoped to exactly that audience (both take `requireUser`, and the card
   * feed's own guard is signed-in-only), so there is no response here that must
   * not carry it, and a narrowed copy would only be a second shape to keep in
   * step.
   *
   * ⚠ **Nothing is added to `ACTIVITY_PAYLOAD_ID_KEYS` for this.** That map is
   * name-keyed and drives `hydrate.ts`'s batched lookups; `overriddenActorUserId`
   * is not resolved to a profile precisely because the sentence does not name the
   * member (one person editing in two tabs is the commonest way to write one of
   * these rows, and "Ada overrode Ada's unseen change" reads as a bug), and
   * adding the key would make every unrelated row that happens to share it fetch
   * a user it never renders.
   *
   * ⚠ **A row whose payload does not parse is served with `null` here, and that
   * is NOT the forbidden-tombstone case one field up.** `comment: null` on a
   * `comment.added` entry is forbidden because "commented on this card" with no
   * comment *is* `FR-COLLAB-003`'s tombstone arriving by another route — the
   * sentence asserts an object that is gone. "overrode an unseen change to the
   * description" asserts nothing about the text, so it stays true with the text
   * missing: nothing is falsified and the entry is not hidden (`FR-COLLAB-018`
   * AC3). See `apps/server/src/routes/activity.ts`, where the `safeParse` and
   * that argument live.
   */
  fieldOverride: cardFieldOverridePayloadSchema.nullable(),
  /**
   * How many raw entries this one stands for — `FR-COLLAB-017`'s collapse edge
   * case. **Always ≥ 1**, and `1` is the overwhelmingly ordinary value.
   *
   * ⚠ **A collapsed entry is the NEWEST of its run**, so its `id`, `createdAt`
   * and tokens are that row's, and the entries behind it are not retrievable from
   * this response. That is safe only because the run collapsed on **byte-identical
   * rendered sentences** (see `ACTIVITY_COLLAPSE_WINDOW_MS`): the surviving
   * sentence is true of every member of the run, so nothing is falsified — which
   * would stop being true the moment a curated "these types are noisy" list
   * replaced the identity test.
   *
   * ⚠ **The argument holds only for entries whose sentence is the whole entry,
   * which is why the two fields above never collapse.** `comment` and
   * `fieldOverride` each carry an object the sentence cannot represent, so for
   * those two types the sentence *under-determines* the row and a collapse would
   * delete a comment's body or an overridden draft outright. Both exceptions are
   * implemented in one place — `joinsRun` in
   * `apps/server/src/activity/collapse.ts`, whose header argues them — and any
   * third payload-bearing field added here owes a third clause there.
   */
  collapsedCount: z.number().int().min(1),
  /**
   * SA-7 — `true` when the actor was a platform superadmin **without membership
   * of the event's board** at the moment the event was written, and **absent**
   * otherwise. Never `false`: the key is present only when it says something.
   *
   * Read off `activity_entries.payload.viaSuperadmin`, which
   * `apps/server/src/superadmin/reach.ts` stamps inside the mutation spine
   * directly above `insertEvents`. It records a fact about the write and is
   * not recomputed on read, so a superadmin who later joins the board keeps
   * the badge on what they did before joining.
   *
   * ⚠⚠ **WIDENED IN PLACE, NOT ON A COPY, AND THAT IS DELIBERATE.** CLAUDE.md's
   * rule is *extend a shipped schema on a copy, never widen it in place*,
   * because one schema serialises several routes and widening it changes
   * responses that must not carry the field. `fieldOverride` above is the
   * precedent for why that rule does not bite here, and the same two tests
   * settle it:
   *
   *   - **Is there a response that must not carry it?** No. Every route that
   *     serialises this entry — the card, board and workspace feeds and the
   *     profile page — may carry it: the flag names no row, only that the
   *     entry's own actor acted through the superadmin arm, and the actor is
   *     already on the entry. A narrowed copy would be a second shape that the
   *     four routes must keep in step, for no audience that needs protecting.
   *   - **Does it change a response that exists today?** No. Absent-when-false
   *     means every entry by an ordinary account serialises byte-identically to
   *     the shape before SA-7, and `z.literal(true).optional()` makes a producer
   *     that writes `false` a parse error rather than a silent one-key diff.
   *     Before this field, `z.object` stripped the key silently; a repository
   *     grep for `viaSuperadmin` found no producer, so nothing moved.
   *
   * ⚠ **A strict payload reader must strip it.** The payload is `z.record` in
   * `./events`, so no event contract refuses the key; the one strict reader is
   * `apps/server/src/undo/planner.ts`, which strips it before its
   * `z.strictObject` parse. A strict payload parser added later that forgets
   * to would refuse rows written by superadmins only, with every test written
   * by an ordinary account still green.
   *
   * ⚠ **A third payload-bearing clause.** Two entries that differ only in this
   * key render byte-identical sentences, so `joinsRun` in
   * `apps/server/src/activity/collapse.ts` refuses to collapse across it:
   * otherwise a member's edit could absorb a superadmin's, and the badge
   * would disappear from the run.
   */
  viaSuperadmin: z.literal(true).optional(),
});

export type ActivityEntry = z.infer<typeof activityEntrySchema>;

/**
 * One page of either feed — `GET /cards/:id/activity` and
 * `GET /boards/:id/activity`.
 *
 * ⚠⚠ **A SHORT PAGE DOES NOT MEAN THE END, AND `nextCursor` IS THE ONLY THING
 * THAT DOES.** Three separate mechanisms remove entries *after* the `LIMIT`:
 * collapsing, the orphaned-comment drop, and (on the board feed) nothing else —
 * but the first two are enough. So `entries.length < limit` is a routine outcome
 * in the middle of history, and a client that stops paging on it loses the rest
 * of the feed with no error anywhere. `FR-COLLAB-020` AC3's "Load more activity"
 * must be driven by `nextCursor !== null` alone.
 *
 * ⚠ **`nextCursor` is `null` when there was no `limit + 1`th RAW row — not when
 * the page came back short.** Emitting one whenever the page looked full gives a
 * "Load more" that returns an empty page at every exact multiple of `limit`;
 * emitting one whenever entries were dropped gives a button that never goes away.
 *
 * ⚠ **There is no total and no count**, deliberately, and for a reason
 * `./search` states one route over: a number is a disclosure. There is also no
 * retention window — **`FR-COLLAB-020`'s "verify before committing to a retention
 * depth" is answered by imposing none**: nothing deletes `activity_entries`, SC-1's
 * whole argument is that a history never recorded cannot be recovered, and a feed
 * that silently stops is worse than a long one. The cursor pages back to the first
 * entry. Recorded in `docs/requirements/03-collaboration.md` rather than only here.
 */
export const activityFeedSchema = z.object({
  /** Newest first. `[]` is a legitimate answer. */
  entries: z.array(activityEntrySchema),
  nextCursor: activityCursorSchema.nullable(),
});

export type ActivityFeed = z.infer<typeof activityFeedSchema>;

/**
 * One entry in `GET /workspaces/:id/activity`.
 *
 * Extends a copy of the shipped B28 entry shape with the board label
 * `FR-COLLAB-021` requires. `activityEntrySchema` itself stays unchanged so the
 * card and board feeds do not acquire a field their routes cannot populate.
 */
export const workspaceActivityEntrySchema = activityEntrySchema.extend({
  board: activityBoardRefSchema,
});

export type WorkspaceActivityEntry = z.infer<typeof workspaceActivityEntrySchema>;

export const workspaceActivityFeedSchema = z.object({
  entries: z.array(workspaceActivityEntrySchema),
  nextCursor: activityCursorSchema.nullable(),
});

export type WorkspaceActivityFeed = z.infer<typeof workspaceActivityFeedSchema>;
